// src/pages/Attendance/EmployeeAttendance.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Clock,
    Calendar,
    CheckCircle,
    AlertCircle,
    X,
    ClipboardCheck,
    List,
    TrendingUp,
    ChevronDown,
    ChevronUp,
    Download,
    Filter,
    RotateCcw, // Icon for clear filter
} from 'lucide-react';
import { format, parseISO, subDays } from 'date-fns';
import { PieChart, Pie, Cell, Legend, ResponsiveContainer } from 'recharts';
import apiWithAuth from '../../services/apiWithAuth';

// --- Helper to format Date objects to YYYY-MM-DD string ---
const formatDateForInput = (date) => format(date, 'yyyy-MM-dd');

// --- Helper to calculate the current month range (00:00:00 time) ---
const calculateDefaultDates = () => {
    const now = new Date();
    // Start of the current month
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    start.setHours(0, 0, 0, 0);

    // Today's date
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    end.setHours(0, 0, 0, 0); // Sets time to 00:00:00

    return {
        defaultStart: formatDateForInput(start),
        defaultEnd: formatDateForInput(end)
    };
};

// --- Custom Label Component for Pie/Donut Center ---
const CustomLabel = ({ viewBox, totalDays }) => {
    if (!viewBox) return null;
    const cx = viewBox.width * 0.3; // 30% of the ResponsiveContainer width
    const cy = viewBox.height * 0.5;
    return (
        <text
            x={cx}
            y={cy}
            fill="#333"
            textAnchor="middle"
            dominantBaseline="central"
        >
            <tspan x={cx} dy="-0.5em" fontSize="24" fontWeight="bold">
                {totalDays}
            </tspan>
            <tspan x={cx} dy="1.2em" fontSize="14" fill="#666">
                Total Days
            </tspan>
        </text>
    );
};

// --- 1. Chart Component (Donut Style Match) ---
function AttendanceSummaryChart({ summary }) {
    // Note: Half-Day is included in data but excluded from the visual legend 
    // to match the requested simplified chart style (Present, Absent, Leave).
    const rawData = [
        { name: 'Present', value: summary.present, color: '#3B82F6' }, // Blue color for Present
        { name: 'Absent', value: summary.absent, color: '#EF4444' },   // Red color for Absent
        { name: 'Leave', value: summary.leave, color: '#10B981' },     // Green color for Leave
        { name: 'Half Day', value: summary.halfDay, color: '#F59E0B' }, // Amber (Will be present in chart if value > 0)
    ];

    // Filter out zero values and Half Day from the primary display set
    const displayData = rawData.filter(d => d.value > 0 && d.name !== 'Half Day');
    const totalDays = summary.present + summary.absent + summary.leave + summary.halfDay;

    const CHART_CX_PERCENT = '30%';
    const CHART_CY_PERCENT = '50%';

    return (
        <div className="bg-white p-4 rounded shadow-md border border-gray-200 h-full">
            <h3 className="font-semibold text-lg text-gray-800 flex items-center gap-2 mb-3">
                <TrendingUp size={18} /> Attendance Distribution
            </h3>

            {totalDays > 0 ? (
                <div style={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={displayData}
                                cx={CHART_CX_PERCENT} // Use fixed center x
                                cy={CHART_CY_PERCENT} // Use fixed center y
                                innerRadius={50}
                                outerRadius={80}
                                fill="#8884d8"
                                paddingAngle={2}
                                dataKey="value"
                                labelLine={false}
                                label={({ value, percent }) => `${value} (${(percent * 100).toFixed(1)}%)`}
                                isAnimationActive={false}
                            >
                                {displayData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={entry.color}
                                    />
                                ))}
                            </Pie>
                            <Legend
                                iconType="circle"
                                layout="vertical" // Changed back to vertical to keep the legend to the right
                                align="right"
                                verticalAlign="middle"
                                wrapperStyle={{ paddingLeft: '10px' }}
                                formatter={(value, entry) => {
                                    const item = rawData.find(d => d.name === value);
                                    const percent = (item.value / totalDays) * 100;
                                    return `${value} ${item.value} (${percent.toFixed(1)}%)`;
                                }}
                            />

                            {/* FIX 3: Render CustomLabel using the chart's dimensions */}
                            <CustomLabel totalDays={totalDays} />

                        </PieChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <div className="text-center py-6 text-gray-500">No data available for the chart.</div>
            )}
        </div>
    );
}


export default function EmployeeAttendance() {
    const { defaultStart, defaultEnd } = calculateDefaultDates();

    // State for data fetching
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Date Filter State: Default to current month start and today, 00:00:00
    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);

    // State for API data
    const [attendanceData, setAttendanceData] = useState({
        period: { startDate: null, endDate: null },
        attendance: [],
        today: { isMarked: false, status: null },
        allowedStatuses: [],
        summary: { totalDays: 0, present: 0, absent: 0, halfDay: 0, leave: 0, totalWorkHours: 0 }
    });

    // State for Marking Attendance
    const [markLoading, setMarkLoading] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('present');
    const [notes, setNotes] = useState('');
    const [showHistory, setShowHistory] = useState(true);

    // --- Helper to calculate summary from the fetched attendance array ---
    const calculateSummary = useCallback((records) => {
        const summary = {
            totalDays: records.length,
            present: records.filter(a => a.status === 'present').length,
            absent: records.filter(a => a.status === 'absent').length,
            halfDay: records.filter(a => a.status === 'half-day').length,
            leave: records.filter(a => a.status === 'leave').length,
            totalWorkHours: records.reduce((sum, a) => sum + (a.workHours || 0), 0)
        };
        return summary;
    }, []);

    // --- Fetch Attendance Data (main API call) ---
    const fetchAttendance = useCallback(async (start, end) => {
        setLoading(true);
        setError(null);
        try {
            // Convert to ISO 8601 string at midnight UTC as required by backend (e.g., 2025-12-01T00:00:00.000Z)
            const startISO = parseISO(start).toISOString();
            const endISO = parseISO(end).toISOString();

            const resp = await apiWithAuth.get(`/admin-panel/attendance/me/attendance?startDate=${startISO}&endDate=${endISO}`);

            if (resp.data.success) {
                const fetchedData = resp.data.data;
                const summary = calculateSummary(fetchedData.attendance);

                setAttendanceData({
                    ...fetchedData,
                    summary: summary
                });
            } else {
                throw new Error(resp.data.message || 'Failed to fetch attendance');
            }
        } catch (err) {
            console.error('Error fetching attendance:', err);
            setError(err.response?.data?.message || err.message || 'Failed to load attendance data');
        } finally {
            setLoading(false);
        }
    }, [calculateSummary]);

    // Trigger fetch when dates change
    useEffect(() => {
        if (startDate && endDate) {
            fetchAttendance(startDate, endDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    // Clear success/error message after a timeout
    useEffect(() => {
        if (successMessage) {
            const t = setTimeout(() => setSuccessMessage(''), 4000);
            return () => clearTimeout(t);
        }
    }, [successMessage]);

    // --- Handle Mark Attendance Submission ---
    const handleMarkAttendance = async (e) => {
        e.preventDefault();
        setMarkLoading(true);
        setError(null);
        setSuccessMessage('');

        try {
            const payload = {
                status: selectedStatus,
                notes: notes,
            };

            const res = await apiWithAuth.post('/admin-panel/attendance/me/attendance/mark', payload);

            if (res.data.success) {
                if (res.data.alreadyMarked) {
                    setError('Attendance is already marked for today.');
                } else {
                    setSuccessMessage(`Attendance marked as '${selectedStatus}' successfully!`);
                    await fetchAttendance(startDate, endDate);
                }
            } else {
                throw new Error(res.data.message || 'Failed to mark attendance');
            }
        } catch (err) {
            console.error('Error marking attendance:', err);
            setError(err.response?.data?.message || err.message || 'Failed to mark attendance');
        } finally {
            setMarkLoading(false);
        }
    };

    const periodDisplay = useMemo(() => {
        if (!startDate || !endDate) return 'Loading...';
        const start = format(parseISO(startDate), 'MMM dd, yyyy');
        const end = format(parseISO(endDate), 'MMM dd, yyyy');
        return `${start} to ${end}`;
    }, [startDate, endDate]);

    // --- Clear Filter Function ---
    const handleClearFilter = () => {
        const { defaultStart: newStart, defaultEnd: newEnd } = calculateDefaultDates();
        setStartDate(newStart);
        setEndDate(newEnd);
    };

    // --- Implement PDF Download Logic ---
    const handleDownloadReport = async () => {
        setError(null);
        try {
            setLoading(true);

            // Format dates to ISO 8601 string at midnight UTC as required by backend
            const startISO = parseISO(startDate).toISOString();
            const endISO = parseISO(endDate).toISOString();

            const res = await apiWithAuth.get(`/admin-panel/attendance/me/attendance/report/pdf?startDate=${startISO}&endDate=${endISO}`, {
                responseType: 'blob' // Crucial for handling binary data like PDFs
            });

            // Create a blob from the response data
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            // Create a link element and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Attendance_Report_${startDate}_to_${endDate}.pdf`);
            document.body.appendChild(link);
            link.click();

            // Clean up
            link.remove();
            window.URL.revokeObjectURL(url);

            setSuccessMessage("PDF report downloaded successfully.");

        } catch (err) {
            console.error('Error downloading PDF:', err);
            setError(err.response?.data?.message || 'Failed to download PDF report. Check server logs.');
        } finally {
            setLoading(false);
        }
    };

    const renderTodayStatus = () => {
        const { isMarked, status } = attendanceData.today;

        if (loading) {
            return <div className="text-center p-4 text-gray-600">Checking today's status...</div>;
        }

        if (isMarked) {
            let colorClass = '';
            switch (status) {
                case 'present':
                    colorClass = 'bg-green-100 text-green-700 border-green-300';
                    break;
                case 'leave':
                    colorClass = 'bg-yellow-100 text-yellow-700 border-yellow-300';
                    break;
                case 'half-day':
                    colorClass = 'bg-blue-100 text-blue-700 border-blue-300';
                    break;
                default:
                    colorClass = 'bg-gray-100 text-gray-700 border-gray-300';
            }
            return (
                <div className={`p-4 rounded-lg border-2 ${colorClass} flex items-center justify-between`}>
                    <div className="flex items-center gap-3">
                        <CheckCircle size={24} />
                        <span className="font-semibold text-lg">
                            Attendance **MARKED** for today.
                        </span>
                    </div>
                    <span className="text-xl font-bold uppercase">{status}</span>
                </div>
            );
        }

        return (
            <div className="p-4 rounded-lg border-2 border-red-300 bg-red-50 text-red-700 flex items-center gap-3">
                <Clock size={24} />
                <span className="font-semibold text-lg">
                    Attendance **NOT MARKED** for today. Please mark below.
                </span>
            </div>
        );
    };

    const renderMarkForm = () => {
        if (attendanceData.today.isMarked || loading) return null;

        return (
            <div className="mt-6 p-6 bg-white rounded-lg shadow-xl border border-blue-200">
                <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">
                    <ClipboardCheck size={20} /> Mark Today's Attendance
                </h2>
                <form onSubmit={handleMarkAttendance} className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        <label className="block text-sm font-medium text-gray-700 w-full sm:w-1/3">
                            Attendance Status <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            className="w-full sm:w-2/3 p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            required
                            disabled={markLoading}
                        >
                            <option value="present">Present</option>
                            <option value="half-day">Half Day</option>
                            <option value="leave">On Leave</option>
                        </select>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        <label className="block text-sm font-medium text-gray-700 w-full sm:w-1/3">
                            Notes (optional)
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows="2"
                            className="w-full sm:w-2/3 p-2 border border-gray-300 rounded-md"
                            disabled={markLoading}
                        />
                    </div>

                    <button
                        type="submit"
                        className="w-full sm:w-auto px-6 py-2 rounded-md bg-blue-600 text-white font-semibold hover:bg-blue-700 transition duration-150 disabled:opacity-50 flex items-center justify-center gap-2"
                        disabled={markLoading}
                    >
                        {markLoading ? (
                            <>
                                <div className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4" />
                                Marking...
                            </>
                        ) : (
                            <>
                                <ClipboardCheck size={18} /> Mark Attendance
                            </>
                        )}
                    </button>
                </form>
            </div>
        );
    };

    const renderAttendanceHistory = () => {
        const records = attendanceData.attendance;
        if (loading && records.length === 0) {
            return <div className="text-center py-6 text-gray-500">Loading history...</div>;
        }

        if (records.length === 0) {
            return <div className="text-center py-6 text-gray-500">No attendance records found for this period.</div>;
        }

        return (
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check In</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {records.map((record) => {
                            const date = format(new Date(record.date), 'MMM dd, yyyy');
                            const checkInTime = record.checkIn ? format(new Date(record.checkIn), 'HH:mm:ss') : '-';
                            const statusColor = {
                                'present': 'bg-green-100 text-green-800',
                                'absent': 'bg-red-100 text-red-800',
                                'half-day': 'bg-blue-100 text-blue-800',
                                'leave': 'bg-yellow-100 text-yellow-800',
                            }[record.status] || 'bg-gray-100 text-gray-800';

                            return (
                                <tr key={record._id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{date}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColor} uppercase`}>
                                            {record.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{checkInTime}</td>
                                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{record.notes || '-'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-6xl mx-auto">
                {/* Header and Global Filter */}
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900 flex items-center gap-2">
                            <Calendar size={30} className="text-blue-600" /> My Attendance
                        </h1>
                        <p className="text-sm text-gray-600">
                            View and mark your daily attendance status.
                        </p>
                    </div>

                    {/* --- GLOBAL DATE FILTER --- */}
                    <div className="flex sm:flex-row items-end sm:items-center gap-3 p-3 bg-white rounded-lg shadow-md border">
                        <div className="flex items-center gap-2">
                            <Filter size={16} className="text-gray-500 hidden sm:block" />
                            <label className="text-sm font-medium text-gray-700">From:</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="p-2 border border-gray-300 rounded-md text-sm w-32"
                                max={endDate}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700">To:</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="p-2 border border-gray-300 rounded-md text-sm w-32"
                                min={startDate}
                            />
                        </div>
                        <button
                            onClick={handleClearFilter}
                            className="flex items-center gap-2 px-3 py-2 rounded bg-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-300 transition"
                        >
                            <RotateCcw size={16} /> Clear
                        </button>
                    </div>
                </div>

                {/* Messages */}
                {successMessage && (
                    <div className="mb-4 p-3 rounded bg-green-50 border border-green-200 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <CheckCircle className="text-green-600" />
                            <div className="text-green-800 font-medium">{successMessage}</div>
                        </div>
                        <button onClick={() => setSuccessMessage('')}>
                            <X size={16} />
                        </button>
                    </div>
                )}

                {error && (
                    <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <AlertCircle className="text-red-600" />
                            <div className="text-red-800 font-medium">{error}</div>
                        </div>
                        <button onClick={() => setError(null)}>
                            <X size={16} />
                        </button>
                    </div>
                )}

                {/* Today's Status */}
                <div className="mb-6 p-4 bg-white rounded-lg shadow-md border">
                    {renderTodayStatus()}
                </div>

                {/* Mark Attendance Form */}
                {renderMarkForm()}

                {/* --- Statistics and Chart --- */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 my-6">
                    <div className="lg:col-span-1">
                        <div className="bg-white p-4 rounded shadow-md border border-gray-200 h-full">
                            <h3 className="font-semibold text-lg text-gray-800 mb-3">Summary ({periodDisplay})</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between border-b border-gray-100 py-1">
                                    <span className="capitalize text-gray-600 font-medium">Total Days in Period</span>
                                    <span className="font-bold text-gray-900">{attendanceData.summary.totalDays}</span>
                                </div>
                                <div className="flex justify-between border-b border-gray-100 py-1">
                                    <span className="capitalize text-gray-600 font-medium">Present</span>
                                    <span className="font-bold text-green-600">{attendanceData.summary.present}</span>
                                </div>
                                <div className="flex justify-between border-b border-gray-100 py-1">
                                    <span className="capitalize text-gray-600 font-medium">Absent</span>
                                    <span className="font-bold text-red-600">{attendanceData.summary.absent}</span>
                                </div>
                                <div className="flex justify-between border-b border-gray-100 py-1">
                                    <span className="capitalize text-gray-600 font-medium">Half Day</span>
                                    <span className="font-bold text-blue-600">{attendanceData.summary.halfDay}</span>
                                </div>
                                <div className="flex justify-between border-b border-gray-100 py-1">
                                    <span className="capitalize text-gray-600 font-medium">Leave</span>
                                    <span className="font-bold text-yellow-600">{attendanceData.summary.leave}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-2">
                        <AttendanceSummaryChart summary={attendanceData.summary} />
                    </div>
                </div>

                {/* --- Attendance History Container --- */}
                <div className="mt-6 bg-white p-4 rounded-lg shadow-xl">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 pb-4 border-b">
                        <div className="flex items-center gap-3 mb-3 md:mb-0">
                            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <List size={20} /> Attendance History
                            </h2>
                            <button
                                onClick={() => setShowHistory(p => !p)}
                                className="p-1 rounded-full hover:bg-gray-100"
                            >
                                {showHistory ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </button>
                        </div>

                        {/* Download Button */}
                        <button
                            onClick={handleDownloadReport}
                            className="flex items-center gap-2 px-3 py-2 rounded bg-red-600 text-white font-semibold text-sm hover:bg-red-700 transition disabled:opacity-50"
                            disabled={loading}
                        >
                            <Download size={16} /> Download PDF
                        </button>
                    </div>

                    {/* Displaying Current Period */}
                    <div className="text-sm text-gray-600 mb-4">
                        Showing records for: **{periodDisplay}**
                    </div>

                    {showHistory && (
                        <div className="pt-2">
                            {renderAttendanceHistory()}
                        </div>
                    )}
                </div>
                {/* --- END UPDATED CONTAINER --- */}

            </div>
        </div>
    );
}